import { bootWebUI } from "./boot";
import { createWebUIElements } from "./elements";
import { bindBridgeMethod, installLegacyBridge, type LegacyMethods } from "./legacy-bridge";
import {
  addPendingTask,
  formatTaskCardStatus,
  formatTaskStatus,
  markPendingTaskFailed,
  replacePendingTask,
  startRunFeedback,
  startUiClock,
  stopRunFeedback,
  updatePreviewElapsedDisplay,
  updatePromptCount,
  updateTaskElapsedDisplays,
  updateTaskInState,
} from "./runtime-feedback";
import { createDefaultState, DEFAULT_DOCUMENT_TITLE } from "./state-defaults";
import { escapeHtml } from "./webui-utils";

const state = createDefaultState();
const els = createWebUIElements();
const proxy = (name: string) => bindBridgeMethod(name);

function syncActiveLightboxUrls(): void {}

const bridgeMethods: LegacyMethods = {
  addApiProvider: proxy("addApiProvider"),
  addGalleryInput: proxy("addGalleryInput"),
  addImageFiles: proxy("addImageFiles"),
  addPendingTask,
  addReferenceAssetInput: proxy("addReferenceAssetInput"),
  acceptTaskSuccesses: proxy("acceptTaskSuccesses"),
  applyTaskUpdate: proxy("applyTaskUpdate"),
  applyTasksSnapshot: proxy("applyTasksSnapshot"),
  assetSource: proxy("assetSource"),
  backendForAuthSource: proxy("backendForAuthSource"),
  bindFormControlEvents: proxy("bindFormControlEvents"),
  bindOverlayPopoverEvents: proxy("bindOverlayPopoverEvents"),
  bindShellUiEvents: proxy("bindShellUiEvents"),
  bindTaskListControlEvents: proxy("bindTaskListControlEvents"),
  canAddSourceToGallery: proxy("canAddSourceToGallery"),
  categoryLabel: proxy("categoryLabel"),
  categoryPromptRole: proxy("categoryPromptRole"),
  closeAddToGallery: proxy("closeAddToGallery"),
  closeApiSettingsModal: proxy("closeApiSettingsModal"),
  closeConfirmPopover: proxy("closeConfirmPopover"),
  closeGallery: proxy("closeGallery"),
  closePromptPopover: proxy("closePromptPopover"),
  closeSettingsModal: proxy("closeSettingsModal"),
  cleanupSessionSelections: proxy("cleanupSessionSelections"),
  consumeLatestTaskNavigationScrollAnchor: proxy("consumeLatestTaskNavigationScrollAnchor"),
  collectReferenceOutput: proxy("collectReferenceOutput"),
  colorNameForHex: proxy("colorNameForHex"),
  colorSwatchButton: proxy("colorSwatchButton"),
  buildPromptForModel: proxy("buildPromptForModel"),
  createGalleryChip: proxy("createGalleryChip"),
  createPromptSnippetChip: proxy("createPromptSnippetChip"),
  currentApiImagesConcurrency: proxy("currentApiImagesConcurrency"),
  currentApiMode: proxy("currentApiMode"),
  currentApiProviderId: proxy("currentApiProviderId"),
  currentApiProviderLabel: proxy("currentApiProviderLabel"),
  currentAuthSource: proxy("currentAuthSource"),
  currentCodexMode: proxy("currentCodexMode"),
  currentMainModel: proxy("currentMainModel"),
  currentPromptFidelity: proxy("currentPromptFidelity"),
  currentPromptForModel: proxy("currentPromptForModel"),
  currentSize: proxy("currentSize"),
  currentTaskParams: proxy("currentTaskParams"),
  customSizeValidationMessage: proxy("customSizeValidationMessage"),
  deleteApiProvider: proxy("deleteApiProvider"),
  deleteTask: proxy("deleteTask"),
  escapeHtml,
  favoriteColorsForDisplay: proxy("favoriteColorsForDisplay"),
  findGalleryItem: proxy("findGalleryItem"),
  firstVisibleTaskId: proxy("firstVisibleTaskId"),
  formatTaskCardStatus,
  formatTaskStatus,
  galleryInputs: proxy("galleryInputs"),
  gallerySource: proxy("gallerySource"),
  getPromptText: proxy("getPromptText"),
  handleAuthSourceClick: proxy("handleAuthSourceClick"),
  handleCustomDimensionInput: proxy("handleCustomDimensionInput"),
  handleQuickGalleryBoundaryWheel: proxy("handleQuickGalleryBoundaryWheel"),
  handleQuickGalleryCategoryEvent: proxy("handleQuickGalleryCategoryEvent"),
  imageFileFromUrl: proxy("imageFileFromUrl"),
  importColorPalette: proxy("importColorPalette"),
  isDirectApiMode: proxy("isDirectApiMode"),
  isQueueDispatchPending: proxy("isQueueDispatchPending"),
  isEditableImageSource: proxy("isEditableImageSource"),
  isTaskArchived: proxy("isTaskArchived"),
  markPendingTaskFailed,
  markTaskViewed: proxy("markTaskViewed"),
  migrateLegacyArchivedTasks: proxy("migrateLegacyArchivedTasks"),
  missingGalleryInputs: proxy("missingGalleryInputs"),
  missingReferenceAssetInputs: proxy("missingReferenceAssetInputs"),
  normalizeApiImagesConcurrency: proxy("normalizeApiImagesConcurrency"),
  normalizeApiSettings: proxy("normalizeApiSettings"),
  normalizeHexColor: proxy("normalizeHexColor"),
  notifyLatestTaskAvailable: proxy("notifyLatestTaskAvailable"),
  notifyTaskUpdate: proxy("notifyTaskUpdate"),
  openAddToGallery: proxy("openAddToGallery"),
  openApiSettingsModal: proxy("openApiSettingsModal"),
  openConfirmPopover: proxy("openConfirmPopover"),
  openGallery: proxy("openGallery"),
  openPromptPopover: proxy("openPromptPopover"),
  openSettingsModal: proxy("openSettingsModal"),
  persistApiSettings: proxy("persistApiSettings"),
  persistMainModel: proxy("persistMainModel"),
  populateApiSettingsForm: proxy("populateApiSettingsForm"),
  populateSettingsForm: proxy("populateSettingsForm"),
  positionPromptPopover: proxy("positionPromptPopover"),
  readApiSettingsForm: proxy("readApiSettingsForm"),
  recentColorsForDisplay: proxy("recentColorsForDisplay"),
  refreshApiSettings: proxy("refreshApiSettings"),
  refreshColorPalette: proxy("refreshColorPalette"),
  refreshGallery: proxy("refreshGallery"),
  refreshHealth: proxy("refreshHealth"),
  refreshPromptTemplates: proxy("refreshPromptTemplates"),
  refreshPromptSnippets: proxy("refreshPromptSnippets"),
  refreshRecentAssets: proxy("refreshRecentAssets"),
  refreshSettings: proxy("refreshSettings"),
  refreshTasks: proxy("refreshTasks"),
  revealActiveTaskGroup: proxy("revealActiveTaskGroup"),
  retryFailedTask: proxy("retryFailedTask"),
  referenceAssetInputs: proxy("referenceAssetInputs"),
  rememberLatestTaskNavigationBeforeRender: proxy("rememberLatestTaskNavigationBeforeRender"),
  rememberRecentColor: proxy("rememberRecentColor"),
  removeBatchSelectedTaskId: proxy("removeBatchSelectedTaskId"),
  removeFavoriteColor: proxy("removeFavoriteColor"),
  renderArchiveButton: proxy("renderArchiveButton"),
  renderArchiveModal: proxy("renderArchiveModal"),
  renderAuthSource: proxy("renderAuthSource"),
  renderBatchToolbar: proxy("renderBatchToolbar"),
  ensureSelectedTaskDetail: proxy("ensureSelectedTaskDetail"),
  renderGalleryCategoryControls: proxy("renderGalleryCategoryControls"),
  renderImageStrip: proxy("renderImageStrip"),
  renderPreview: proxy("renderPreview"),
  renderTaskHistoryAnchors: proxy("renderTaskHistoryAnchors"),
  renderReferenceCollector: proxy("renderReferenceCollector"),
  renderTasks: proxy("renderTasks"),
  scheduleLatestTaskNavigationRefresh: proxy("scheduleLatestTaskNavigationRefresh"),
  replacePendingTask,
  replaceTask: proxy("replaceTask"),
  restoreApiSettings: proxy("restoreApiSettings"),
  restoreExpandedTaskGroupKey: proxy("restoreExpandedTaskGroupKey"),
  restoreLegacyArchivedTasks: proxy("restoreLegacyArchivedTasks"),
  restoreMainModel: proxy("restoreMainModel"),
  restoreSidebarWidth: proxy("restoreSidebarWidth"),
  restoreThemePreference: proxy("restoreThemePreference"),
  revokeTaskUploadPreviewUrls: proxy("revokeTaskUploadPreviewUrls"),
  revokeUploadPreviewUrl: proxy("revokeUploadPreviewUrl"),
  revokeUploadPreviewUrls: proxy("revokeUploadPreviewUrls"),
  runTask: proxy("runTask"),
  saveApiSettings: proxy("saveApiSettings"),
  saveFavoriteColor: proxy("saveFavoriteColor"),
  saveSettings: proxy("saveSettings"),
  saveUploadToGallery: proxy("saveUploadToGallery"),
  scheduleQuickGalleryFocusUpdate: proxy("scheduleQuickGalleryFocusUpdate"),
  queueApiSettingsAutosave: proxy("queueApiSettingsAutosave"),
  selectCodexMode: proxy("selectCodexMode"),
  selectTask: proxy("selectTask"),
  ensureExpandedTaskGroupKey: proxy("ensureExpandedTaskGroupKey"),
  setMode: proxy("setMode"),
  setExpandedTaskGroupKey: proxy("setExpandedTaskGroupKey"),
  setPromptText: proxy("setPromptText"),
  setPromptWithGalleryRefs: proxy("setPromptWithGalleryRefs"),
  setStatus: proxy("setStatus"),
  setTaskArchiveState: proxy("setTaskArchiveState"),
  sourceName: proxy("sourceName"),
  sourcePreviewUrl: proxy("sourcePreviewUrl"),
  startRunFeedback,
  startUiClock,
  stopRunFeedback,
  scrollExpandedTaskGroupToTop: proxy("scrollExpandedTaskGroupToTop"),
  syncActiveLightboxUrls,
  syncGalleryInputsFromPrompt: proxy("syncGalleryInputsFromPrompt"),
  syncPromptFromEditor: proxy("syncPromptFromEditor"),
  syncPromptGalleryMentionsFromInputs: proxy("syncPromptGalleryMentionsFromInputs"),
  syncCodexModeNotes: proxy("syncCodexModeNotes"),
  syncRadioButtons: proxy("syncRadioButtons"),
  syncSizeControlsFromSize: proxy("syncSizeControlsFromSize"),
  taskApiProviderId: proxy("taskApiProviderId"),
  taskApiProviderLabel: proxy("taskApiProviderLabel"),
  taskBackendLabel: proxy("taskBackendLabel"),
  taskHasViewableUpdate: proxy("taskHasViewableUpdate"),
  taskRetryStateText: proxy("taskRetryStateText"),
  taskTotalCount: proxy("taskTotalCount"),
  toggleColorPaletteManageMode: proxy("toggleColorPaletteManageMode"),
  updateCompression: proxy("updateCompression"),
  applyFirstReferenceImageAspectRatio: proxy("applyFirstReferenceImageAspectRatio"),
  updateCustomRatioFieldState: proxy("updateCustomRatioFieldState"),
  updateCustomRatioReferenceButtonState: proxy("updateCustomRatioReferenceButtonState"),
  updateCustomSize: proxy("updateCustomSize"),
  updateDocumentTitle: proxy("updateDocumentTitle"),
  addImages: proxy("addImages"),
  clearImages: proxy("clearImages"),
  updateImageStripDensity: proxy("updateImageStripDensity"),
  updatePixelPreview: proxy("updatePixelPreview"),
  updatePreviewElapsedDisplay,
  updatePromptCount,
  updateQuantity: proxy("updateQuantity"),
  updateRequestPreview: proxy("updateRequestPreview"),
  updateSizeFromPreset: proxy("updateSizeFromPreset"),
  updateTaskElapsedDisplays,
  updateTaskInState,
  uploadInputs: proxy("uploadInputs"),
  uploadSource: proxy("uploadSource"),
};

export function initializeLegacyBridge(): void {
  installLegacyBridge({
    state,
    els,
    boot: () => bootWebUI(state, els, bridgeMethods),
    constants: {
      defaultDocumentTitle: DEFAULT_DOCUMENT_TITLE,
    },
    methods: bridgeMethods,
  });
}
